// ============================================================================
// Stories — segmentation math (frontend-only, no backend)
// ============================================================================
//
// Single source of truth for "given a video duration in seconds, how many
// Story segments will it become?". The rule (founder-approved Mar 2026):
//
//     1 segment = 60 seconds (matches Instagram Stories current model)
//
//     30  s  → 1 historia
//     70  s  → 2 historias        (60 + 10)
//     150 s  → 3 historias        (60 + 60 + 30)
//
// PURE FUNCTIONS. No DOM, no async, no I/O. Everything else (the UI,
// the preview cards, the trim dialog) imports from here so the math
// can never drift between components.

import { tr } from '@/contexts/LanguageContext';

export const STORY_SEGMENT_SECONDS = 60;
export const CAMERA_MAX_SECONDS = 60;

/** ceil(duration / 60) — minimum 1 even for 0s edge cases. */
export const segmentCountFor = (totalSeconds) => {
  const total = Math.max(0, Number(totalSeconds) || 0);
  if (total <= STORY_SEGMENT_SECONDS) return 1;
  return Math.ceil(total / STORY_SEGMENT_SECONDS);
};

/**
 * Returns the [start, end] windows (in seconds, inclusive of start, exclusive
 * of end except for the last which clamps to total) for each segment.
 *
 *   ranges(30)   → [[0, 30]]
 *   ranges(70)   → [[0, 60], [60, 70]]
 *   ranges(150)  → [[0, 60], [60, 120], [120, 150]]
 */
export const segmentRangesFor = (totalSeconds) => {
  const total = Math.max(0, Number(totalSeconds) || 0);
  const out = [];
  let cur = 0;
  while (cur < total) {
    const end = Math.min(cur + STORY_SEGMENT_SECONDS, total);
    out.push([cur, end]);
    cur = end;
  }
  if (out.length === 0) out.push([0, 0]);
  return out;
};

/** mm:ss for the UI labels. Defensive against NaN. */
export const formatClock = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

/** Spanish copy for the segments banner — kept here so it can't drift. */
export const segmentBannerCopy = (count) => {
  if (count <= 1) return tr("Se publicará como una sola historia.");
  return tr("Este video se dividirá en {0} historias.", count);
};
